<template>
    <router-link :to="detailsRoute" tag="a"
        class="search-results-vehicle" @click.stop.native="'Business - Results', 'Clicked', 'Card'" :id="elementId">
        <div class="gallery-image-wrapper" v-if="paintCount > 0">
            <!-- Outer container reserves 16:10 aspect ratio to avoid layout shift -->
            <div class="gallery-image-outer">
                <div class="gallery-image-inner">
                    <picture>
                        <source :srcset="'https://cdn.imagin.studio/getImage?customer=robinsandday&' + currentGalleryImage + '&zoomType=fullscreen&steering=right&width=800&angle=01&fileType=webp'" type="image/webp">
                        <img class="img-responsive"
                            :src="'https://cdn.imagin.studio/getImage?customer=robinsandday&' + currentGalleryImage + '&zoomType=fullscreen&steering=right&width=800&angle=01&fileType=png'"
                            :key="currentGalleryImage"
                            @load="onGalleryImageLoad"
                            @error="onGalleryImageError"
                            itemprop="thumbnail"
                            alt="">
                    </picture>
                </div>

                <!-- loading overlay shown while the image is loading -->
                <div class="gallery-image-overlay" v-if="imageLoading">
                    <div class="gallery-spinner" role="status" aria-live="polite" aria-label="Loading image">
                        <svg viewBox="0 0 50 50" class="spinner-svg" xmlns="http://www.w3.org/2000/svg">
                            <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
                        </svg>
                    </div>
                </div>

                <!-- error placeholder if load fails -->
                <div class="gallery-image-overlay gallery-error" v-if="imageLoadError">
                    <div class="gallery-error-inner">Image failed to load</div>
                </div>
            </div>

            <div class="search-results-vehicle__exclusive-offer-badge" v-if="exclusiveOffer"><img src="/images/business-and-fleet/star.svg" alt="Exclusive Offer">Exclusive Offer</div>
        </div>
        <div class="search-results-vehicle__swiper-container" v-if="paintCount > 1" @click.stop.prevent="">
            <div class="search-results-vehicle__swiper-btn search-results-vehicle__swiper-prev" ref="colour-prev"></div>
            <div class="swiper search-results-vehicle__colour-swiper" ref="colourSwiper">
                <div class="swiper-wrapper">
                    <div class="swiper-slide" v-for="paint in vehicle.new_vehicle_paint_options" :key="'paint-' + paint.cap_paint_id">
                        <button type="button" @click.stop.prevent="setSelectedPaint(paint)" class="search-results-vehicle__paint-btn" :class="{'search-results-vehicle__paint-btn--active': selectedPaint.cap_paint_id == paint.cap_paint_id}" :title="paint.name">
                            <div class="search-results-vehicle__paint-btn-inner" :style="'background-color: ' + paint.primary_hex"></div>
                        </button>
                    </div>
                </div>
            </div>
            <div class="search-results-vehicle__swiper-btn search-results-vehicle__swiper-next" ref="colour-next"></div>
        </div>
        <div class="search-results-vehicle__content">
            <div class="search-results-vehicle__title-area" @click.stop="gaTag('Business - Result', 'Clicked', 'Title')">
                <h4 class="search-results-vehicle__title">{{ modelTitle }}</h4>
                <h5 class="search-results-vehicle__subtitle">{{ vehicle.bodystyle }} {{ vehicle.version }}</h5>
            </div>

            <div class="search-results-vehicle__pricing-area">
                <div class="search-results-vehicle__payment" v-if="hasMonthlyPricing">
                    <span class="search-results-vehicle__payment-value">£{{ monthlyPrice }}</span>
                    <span class="search-results-vehicle__payment-label">Per month from*</span>
                </div>
                <hr class="search-results-vehicle__separator" v-if="hasMonthlyPricing" />

                <div class="search-results-vehicle__payment" v-if="hasMonthlyPricing">
                    <span class="search-results-vehicle__payment-value">£{{ initialRental }}</span>
                    <span class="search-results-vehicle__payment-label">Initial rental</span>
                </div>
                <hr class="search-results-vehicle__separator" v-if="hasMonthlyPricing" />

                <div class="search-results-vehicle__payment">
                    <span class="search-results-vehicle__payment-value">£{{ cashPrice }}</span>
                    <span class="search-results-vehicle__payment-label">P11D</span>
                </div>

                <router-link :to="detailsRoute" tag="a" class="btn btn--primary">View offer</router-link>
            </div>
        </div>

        <button v-if="showOfferButton" type="button" class="btn btn--primary search-results-vehicle__offers-btn" @click.stop="gaTag('Business - Results', 'Clicked', 'View ' + vehicle.short_name + ' Offers')" @click.prevent.stop="updateModelSelection">View {{ vehicle.short_name }} offers</button>
    </router-link>
</template>

<script>
import numberFormat from '../../core/modules/number-format';
import Swiper, { Navigation } from 'swiper';

// import NumberFormat from '../../used-vue/api/NumberFormat';

export default {
    components: {
    },
    props: {
        vehicle: {
            type: Object,
            required: true,
            default: {},
        },
    },
    data() {
        return {
            imageIndex: 0,
            selectedPaint: {},
            currentStartIndex: 0,
            visibleItems: 4,
            imageLoading: false,
            imageLoadError: false,
            _imageLoadTimer: null,
            colourSwiper: null,
        };
    },
    created() {
        if (this.paintCount > 0) {
            if (this.vehicle.cap_paint_id) {
                const foundPaint = this.vehicle.new_vehicle_paint_options.find(p => p.cap_paint_id === this.vehicle.cap_paint_id);
                if (foundPaint) {
                    this.selectedPaint = foundPaint;
                    this.imageIndex = this.vehicle.new_vehicle_paint_options.indexOf(foundPaint);
                    return;
                }
            }
            this.selectedPaint = this.vehicle.new_vehicle_paint_options[0];
        }
    },
    methods: {
        setSelectedPaint(paint) {
            this.selectedPaint = paint;
            // if swiper is present, ensure the active slide is visible
            try {
                if (this.colourSwiper) {
                    const idx = this.vehicle.new_vehicle_paint_options.findIndex(p => p.cap_paint_id === paint.cap_paint_id);
                    if (idx >= 0) {
                        // slideTo expects slide index; our slides are in same order as options
                        this.colourSwiper.slideTo(idx);
                    }
                }
            } catch (e) {}
        },
        previousImage() {
            if (!this.hasPreviousImage) {
                return;
            }
            this.imageIndex--;
        },
        nextImage() {
            if (!this.hasNextImage) {
                return;
            }
            this.imageIndex++;
        },
        prevPaintOptions() {
            if (this.currentStartIndex > 0) {
                this.currentStartIndex -= this.visibleItems;
                this.currentStartIndex = Math.max(0, this.currentStartIndex);
            }
        },
        nextPaintOptions() {
            if (this.currentStartIndex + this.visibleItems < this.vehicle.new_vehicle_paint_options.length ) {
                this.currentStartIndex += this.visibleItems;
                // Adjust index if we're close to the end
                if (this.currentStartIndex +this.visibleItems > this.vehicle.new_vehicle_paint_options.length) {
                    this.currentStartIndex = this.vehicle.new_vehicle_paint_options.length - this.visibleItems;
                }
            }
        },
        startImageLoadTimeout() {
            // clear any existing timer
            if (this._imageLoadTimer) {
                clearTimeout(this._imageLoadTimer);
                this._imageLoadTimer = null;
            }
            // start timeout to mark as error if image doesn't load in reasonable time
            const TIMEOUT_MS = 8000; // 8 seconds
            this._imageLoadTimer = setTimeout(() => {
                this.imageLoading = false;
                this.imageLoadError = true;
                this._imageLoadTimer = null;
            }, TIMEOUT_MS);
        },
        onGalleryImageLoad(event) {
            // image successfully loaded
            this.imageLoading = false;
            this.imageLoadError = false;
            if (this._imageLoadTimer) {
                clearTimeout(this._imageLoadTimer);
                this._imageLoadTimer = null;
            }
        },
        onGalleryImageError(event) {
            // image failed to load
            this.imageLoading = false;
            this.imageLoadError = true;
            if (this._imageLoadTimer) {
                clearTimeout(this._imageLoadTimer);
                this._imageLoadTimer = null;
            }
        },
        updateModelSelection() {
            this.$store.dispatch('BusinessResults/setUserOptionsByPushingRoute', {
                'manufacturer': [this.vehicle.manufacturer],
                'model': [this.vehicle.short_name],
            });
        },
        updateNavButtons(index, total) {
            const prev = this.$refs['colour-prev'];
            const next = this.$refs['colour-next'];

            prev?.classList.toggle('search-results-vehicle__nav-button--disabled', index === 0);
            next?.classList.toggle('search-results-vehicle__nav-button--disabled', index === total - 1);
        }
    },
    watch: {
        // when the computed currentGalleryImage changes, start the loading state
        currentGalleryImage(newVal, oldVal) {
            // If there is no change or empty value, do nothing
            if (!newVal) {
                this.imageLoading = false;
                this.imageLoadError = false;
                return;
            }

            // Start loading overlay and timeout. The actual <img> has @load/@error handlers that will clear this.
            this.imageLoading = true;
            this.imageLoadError = false;
            this.startImageLoadTimeout();
        }
    },
    mounted() {
        this.$nextTick(() => {
            try {
                if (this.paintCount > 1 && this.$refs['colourSwiper']) {
                    const paints = this.vehicle.new_vehicle_paint_options;
                    this.colourSwiper = new Swiper(this.$refs['colourSwiper'], {
                        modules: [],
                        grabCursor: true,
                        slidesPerView: "auto",
                        spaceBetween: 8,
                        speed: 300,
                        direction: "horizontal",
                        on: {
                            slideChange: () => {
                                const index = this.colourSwiper.activeIndex;
                                const paint = paints[index];

                                if (paint) {
                                    this.setSelectedPaint(paint);
                                }

                                this.updateNavButtons(index, paints.length);
                            }
                        }
                    });

                    const prevBtn = this.$refs['colour-prev'];
                    const nextBtn = this.$refs['colour-next'];

                    if (prevBtn) {
                        prevBtn.addEventListener("click", (e) => {
                            e.preventDefault();

                            let idx = paints.findIndex(
                                p => p.cap_paint_id === this.selectedPaint.cap_paint_id
                            );

                            if (idx === -1) {
                                idx = this.colourSwiper?.activeIndex ?? 0;
                            }

                            const newIdx = Math.max(0, idx - 1);

                            this.setSelectedPaint(paints[newIdx]);
                            this.colourSwiper.slideTo(newIdx);

                            this.updateNavButtons(newIdx, paints.length);
                        });
                    }

                    if (nextBtn) {
                        nextBtn.addEventListener("click", (e) => {
                            e.preventDefault();

                            let idx = paints.findIndex(
                                p => p.cap_paint_id === this.selectedPaint.cap_paint_id
                            );

                            if (idx === -1) {
                                idx = this.colourSwiper?.activeIndex ?? 0;
                            }

                            const newIdx = Math.min(paints.length - 1, idx + 1);

                            this.setSelectedPaint(paints[newIdx]);
                            this.colourSwiper.slideTo(newIdx);

                            this.updateNavButtons(newIdx, paints.length);
                        });
                    }

                    let initialIdx = paints.findIndex(
                        p => p.cap_paint_id === this.selectedPaint.cap_paint_id
                    );

                    if (initialIdx < 0) initialIdx = 0;

                    this.colourSwiper.slideTo(initialIdx);

                    this.updateNavButtons(initialIdx, paints.length);
                }
            } catch (e) {
                // silently fail if swiper not available
                console.info('colour swiper init failed', e);
            }
        });

        // if there's an initial gallery image, start the loading overlay until the <img> fires load/error
        if (this.paintCount > 0 && this.currentGalleryImage) {
            this.imageLoading = true;
            this.imageLoadError = false;
            this.startImageLoadTimeout();
        }
    },
    beforeDestroy() {
        if (this._imageLoadTimer) {
            clearTimeout(this._imageLoadTimer);
            this._imageLoadTimer = null;
        }
        if (this.colourSwiper) {
            try {
                this.colourSwiper.destroy(true, true);
            } catch (e) {}
            this.colourSwiper = null;
        }
    },
    computed: {
        detailsRoute() {
            // Vehicle slug is a URL safe version of the vehicles model title, bodystyle, and version
            const slug = this.modelTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-' + this.vehicle.bodystyle.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-' + this.vehicle.version.toLowerCase().replace(/[^a-z0-9]+/g, '-');
            return {
                name: 'business.offers.show',
                params: {
                    brand: this.vehicle.brand,
                    id: this.vehicle.id,
                    slug: slug,
                },
            };
        },
        showOfferButton() {
            if (this.$store.state.BusinessResults.userOptions.manufacturer.length == 0) {
                return true;
            }
            if (this.$store.state.BusinessResults.userOptions.model.length == 0) {
                return true;
            }

            let manufacturerArrayMatches = true;
            let hasManufacturer = false;
            this.$store.state.BusinessResults.userOptions.manufacturer.forEach(manufacturer => {
                if (manufacturer != this.vehicle.manufacturer) {
                    manufacturerArrayMatches = false;
                    return;
                }
                hasManufacturer = true;
            });
            if (!manufacturerArrayMatches || !hasManufacturer) {
                return true;
            }

            let modelArrayMatches = true;
            let hasModel = false;
            this.$store.state.BusinessResults.userOptions.model.forEach(model => {
                if (model != this.vehicle.short_name) {
                    modelArrayMatches = false;
                    return;
                }
                hasModel = true;
            });
            if (!modelArrayMatches || !hasModel) {
                return true;
            }

            return false;
        },
        currentPaintOptions() {
            return this.vehicle.new_vehicle_paint_options.slice(
                this.currentStartIndex,
                this.currentStartIndex + this.visibleItems
            );
        },
        shouldShowNextButton() {
            const remainingItems = this.vehicle.new_vehicle_paint_options.length - this.currentStartIndex;
            // if there is only one more item show that instead of next button
            if (this.visibleItems + 1 === remainingItems ){
                this.visibleItems += 1;
                return false
            }

            return remainingItems > this.visibleItems;
        },
        selectedPaintName() {
            if (this.selectedPaint && this.selectedPaint.name) {
                return this.selectedPaint.name;
            }
            return 'Select colour';
        },
        modelTitle() {
            if (this.vehicle.fuel_type != 'Electric') {
                return this.vehicle.model_title ?? this.vehicle.model;
            }
            if (this.vehicle.electric_model_title) {
                return this.vehicle.electric_model_title ?? this.vehicle.model;
            }

            return this.vehicle.model_title ?? this.vehicle.model;
        },
        hasMonthlyPricing() {
            return (typeof this.vehicle.cheapest_business_vehicle_finance_offer !== 'undefined' && this.vehicle.cheapest_business_vehicle_finance_offer !== null);
        },
        exclusiveOffer() {
            if (!this.hasMonthlyPricing) {
                return false;
            }
            return (this.vehicle.cheapest_business_vehicle_finance_offer.is_exclusive_offer == 1);
        },
        monthlyPrice() {
            return numberFormat(parseFloat(this.vehicle.cheapest_business_vehicle_finance_offer.monthly_rental));
        },
        initialRental() {
            return numberFormat(parseFloat(this.vehicle.cheapest_business_vehicle_finance_offer.initial_rental));
        },
        cashPrice() {
            return numberFormat(parseFloat(this.vehicle.business_price_exc_vat));
        },
        hasNextImage() {
            if (this.paintCount <= 1) {
                return false;
            }
            return ((this.imageIndex + 1) < this.paintCount);
        },
        hasPreviousImage() {
            if (this.paintCount <= 1) {
                return false;
            }
            return (this.imageIndex > 0);
        },
        currentGalleryImage() {
            if (this.paintCount > 0) {
                return this.selectedPaint.url_identifier;
            }

            return "/images/used/image-coming-soon@2x.jpg";
        },

        paintCount() {
            if (this.vehicle.new_vehicle_paint_options) {
                return this.vehicle.new_vehicle_paint_options.length;
            }
            return 0;
        },

        elementId() {
            return "vehicle-result-" + this.vehicle.id;
        },
    },

};
</script>

<style lang="scss">
@import "../../../sass/redesign/variables";
.search-results-vehicle {
    box-shadow: $default-card-box-shadow-darker-shorter;
    border-radius: $default-card-border-radius;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: stretch;
    align-items: stretch;
    // max-height: 580px;
    transition: transform 150ms ease-in-out, box-shadow 150ms ease-in-out;
    transform: scale(1.0);
    container-name: searchResult;
    container-type: inline-size;

    &:hover {
        transform: scale(1.02);
        box-shadow: $default-card-box-shadow-darker;
        text-decoration: none;
    }

    &__content {
        padding: 12px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: stretch;
        flex-grow: 1;
        gap: 10px;
    }

    &__title-area {
        flex-grow: 1;
    }

    &__title {
        color: #0b1e3a;
        font-weight: 600;
        font-size: 20px;
    }

    &__subtitle {
        color: #0b1e3a;
        font-weight: 400;
        font-size: 16px;
    }

    &__pricing-area {
        background: #ebf9ff;
        padding: 20px 15px;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: space-evenly;
        gap: 10px;
    }
    &__separator {
        border: none;
        border-top: 1px solid #c1c8cb79;
        margin: 0;
    }
    &__payment {
        color: #0b1e3a;
        font-size: 18px;
    }
    &__payment-value {
        font-weight: 600;
    }
    &__payment-label {
        font-weight: 400;
    }

    &__swiper-container {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        margin-bottom: 20px;
        padding-left: 12px;
        padding-right: 12px;
    }
    &__colour-swiper {
        flex-basis: calc(100% - 118px);
        padding: 5px;

        .swiper-slide {
            width: auto;
            display: inline-flex;
            align-items: center;
        }
    }

    &__swiper-btn {
        flex-basis: 49px;
        flex-grow: 0;
        flex-shrink: 0;
        height: 49px;
        cursor: pointer;
        transform: scale(1.0);
        transition: 150ms transform ease-in-out, 150ms opacity ease-in-out;
        &--disabled {
            opacity: 0.3;
            cursor: initial;
        }
        &.swiper-button-lock {
            display: block;
        }
    }

    &__swiper-prev {
        background-image: url(/images/new/details/left-arrow.svg);
        &:hover {
            transform: scale(1.05);
        }
    }

    &__swiper-next {
        background-image: url(/images/new/details/right-arrow.svg);

        &:hover {
            transform: scale(1.05);
        }
    }

    &__paint-btn {
        flex-grow: 0;
        flex-shrink: 0;
        width: 45px;
        height: 45px;
        border: 1px solid #0C93FF;
        border-radius: 45px;
        background-color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: 150ms box-shadow ease-in;
        box-shadow: 0 0 0 0 #0C93FF, inset 0 0 0 0 #0C93FF;

        &:hover {
            box-shadow: 0 0 3px 0 #0C93FF, inset 0 0 3px 0 #0C93FF;
        }

        &--active {
            box-shadow: 0 0 5px 0 #0C93FF, inset 0 0 5px 0 #0C93FF;
        }
    }
    &__paint-btn-inner {
        flex-grow: 0;
        flex-shrink: 0;
        width: 25px;
        height: 25px;
        border-radius: 25px;
        background-color: #DFDFDF;
    }

    &__nav-button--disabled {
        opacity: 0.3;
        pointer-events: none;
    }

    &__offers-btn {
        // background gradient going from #0093d6 to #0b1e3b, top to bottom
        background: linear-gradient(180deg, #0093d6 0%, #0b1e3b 100%);
        border: none;
        padding: 20px;
        height: auto !important;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: center;
        align-items: center;
        gap: 1em;
        white-space: wrap;

        &::after {
            content: url(/images/link-icon-orange.svg);

            transform-origin: center center;
            width: 22px;
            height: 22px;
        }
    }

    &__exclusive-offer-badge {
        position: absolute;
        bottom: 5px;
        left: 20px;
        background: #5EB565;
        border-radius: 4px;
        padding: 5px 7px;
        font-size: 14px;
        font-weight: 600;
        color: #fff;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        align-items: center;
        gap: 6px;

        img {
            width: 19px;
        }
    }
}

.gallery-image-wrapper {
    position: relative;
    width: 100%;
    height: auto;
    display: block;
}

/* Reserve a 16:10 area. Use aspect-ratio where available; fallback to padding-top.
   16:10 = 1.6 -> padding-top = 62.5% (10/16 = 0.625) */
.gallery-image-outer {
    position: relative;
    width: 100%;
    /* modern browsers */
    aspect-ratio: 16 / 10;
}

/* Fallback for browsers that don't support aspect-ratio */
.gallery-image-outer::before {
    content: '';
    display: block;
    padding-top: 62.5%;
}

.gallery-image-inner {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
    border-top-left-radius: $default-card-border-radius;
    border-top-right-radius: $default-card-border-radius;
}

.gallery-image-inner picture,
.gallery-image-inner img {
    width: 100%;
    height: 100%;
}

.gallery-image-inner img.img-responsive {
    display: block;
    object-fit: cover;
    width: 100%;
    height: 100%;
}

.gallery-image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255,255,255,0.6);
    border-top-left-radius: $default-card-border-radius;
    border-top-right-radius: $default-card-border-radius;
}

.gallery-spinner .spinner-svg {
    width: 48px;
    height: 48px;
}

.gallery-spinner .path {
    stroke: #0093D6;
    stroke-linecap: round;
    animation: rotate 1s linear infinite;
}

@keyframes rotate {
    0% { stroke-dasharray: 1, 150; stroke-dashoffset: 0; }
    50% { stroke-dasharray: 90, 150; stroke-dashoffset: -35; }
    100% { stroke-dasharray: 90, 150; stroke-dashoffset: -124; }
}

.gallery-image-overlay.gallery-error {
    background: rgba(0,0,0,0.6);
    color: #fff;
}

.gallery-error-inner {
    padding: 8px 12px;
    background: rgba(0,0,0,0.5);
    border-radius: 4px;
    font-size: 14px;
}
</style>